import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../api/axiosConfig';

export default function MessagesPage() {
    const { userData } = useAuth();
    const isAdmin = userData?.role === 'ADMIN' || userData?.is_staff;
    const currentUserId = userData?.id;

    // Стейты чатов
    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    
    // НОВОЕ: Рефы для умной прокрутки
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    // Стейты глобального поиска
    const [searchMode, setSearchMode] = useState(false); // false = Чаты, true = Поиск
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [searchResults, setSearchResults] = useState([]);
    
    // Настройки приватности и блокировки
    const [isPrivacyMenuOpen, setIsPrivacyMenuOpen] = useState(false);
    const [myChatEnabled, setMyChatEnabled] = useState(true);
    const [isCurrentChatBlocked, setIsCurrentChatBlocked] = useState(false);
    const [chatTargetUserId, setChatTargetUserId] = useState(null); // ID собеседника в ЛС

    // 1. ЗАГРУЗКА ЧАТОВ
    const fetchChats = async () => {
        try {
            const res = await API.get('/api/chats/');
            setChats(res.data);
            setLoading(false);
        } catch (err) { console.error("Ошибка загрузки чатов", err); }
    };

    // 2. ЗАГРУЗКА СООБЩЕНИЙ И ПРОВЕРКА БЛОКИРОВКИ
    const fetchMessages = async () => {
        if (!activeChat) return;
        try {
            const res = await API.get(`/api/chats/${activeChat.id}/messages/`);
            setMessages(res.data);
            if (activeChat.unread > 0) {
                fetchChats();
                setActiveChat(prev => ({ ...prev, unread: 0 }));
            }
            
            // Если это ЛС, определяем ID собеседника и проверяем, заблокирован ли он нами
            if (activeChat.type === 'private') {
                const targetMsg = res.data.find(m => m.sender_id !== currentUserId);
                if (targetMsg) {
                    setChatTargetUserId(targetMsg.sender_id);
                    const blockRes = await API.get(`/api/chats/block-user/${targetMsg.sender_id}/`);
                    setIsCurrentChatBlocked(blockRes.data.is_blocked);
                }
            } else {
                setChatTargetUserId(null);
                setIsCurrentChatBlocked(false);
            }
        } catch (err) { console.error("Ошибка загрузки сообщений", err); }
    };

    // 3. ПОИСК ПОЛЬЗОВАТЕЛЕЙ
    const handleSearchUsers = async () => {
        if (!searchMode) return;
        try {
            const res = await API.get(`/api/chats/search-users/?q=${searchQuery}&role=${roleFilter}`);
            setSearchResults(res.data);
        } catch (error) { console.error("Ошибка поиска", error); }
    };

    useEffect(() => { handleSearchUsers(); }, [searchQuery, roleFilter, searchMode]);

    // Фоновый опрос
    useEffect(() => {
        fetchChats();
        // Получаем статус ЛС юзера
        API.get('/api/me/').then(res => { if(res.data.is_chat_enabled !== undefined) setMyChatEnabled(res.data.is_chat_enabled); }).catch(()=>{});
        const chatInterval = setInterval(fetchChats, 5000);
        return () => clearInterval(chatInterval);
    }, []);

    // НОВОЕ: Принудительная прокрутка при открытии нового чата
    useEffect(() => {
        setShouldAutoScroll(true);
    }, [activeChat?.id]);

    useEffect(() => {
        if (activeChat) {
            fetchMessages();
            const msgInterval = setInterval(fetchMessages, 3000);
            return () => clearInterval(msgInterval);
        }
    }, [activeChat?.id]);

    // НОВОЕ: Умная прокрутка
    useEffect(() => {
        if (!chatContainerRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        // Если до конца чата осталось меньше 150 пикселей, считаем, что мы внизу
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 150;

        // Крутим вниз только если это первая загрузка чата ИЛИ если мы уже читаем самые свежие сообщения
        if (shouldAutoScroll || isAtBottom) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            if (shouldAutoScroll) setShouldAutoScroll(false);
        }
    }, [messages]);

    // 4. ДЕЙСТВИЯ
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeChat) return;
        
        // При отправке собственного сообщения всегда крутим вниз
        setShouldAutoScroll(true);
        
        try {
            await API.post(`/api/chats/${activeChat.id}/messages/`, { text: newMessage });
            setNewMessage('');
            fetchMessages(); 
        } catch (error) { alert(error.response?.data?.error || "Ошибка отправки сообщения"); }
    };

    const handleStartPrivateChat = async (userId) => {
        try {
            const res = await API.post(`/api/chats/private/${userId}/`);
            setSearchMode(false);
            setSearchQuery('');
            await fetchChats();
            setActiveChat({ id: res.data.room_id, type: 'private', name: 'Загрузка...' }); // Временное имя
        } catch (error) { alert(error.response?.data?.error || "Ошибка создания чата"); }
    };

    const handleToggleMyPrivacy = async () => {
        try {
            const res = await API.post('/api/chats/toggle-privacy/');
            setMyChatEnabled(res.data.is_chat_enabled);
            setIsPrivacyMenuOpen(false);
            alert(`Личные сообщения ${res.data.is_chat_enabled ? 'включены' : 'отключены'}.`);
        } catch (error) { alert("Ошибка сохранения настроек"); }
    };

    const handleBlockUser = async () => {
        if (!chatTargetUserId) return;
        try {
            const res = await API.post(`/api/chats/block-user/${chatTargetUserId}/`);
            setIsCurrentChatBlocked(res.data.status === 'blocked');
            alert(res.data.status === 'blocked' ? 'Пользователь заблокирован' : 'Пользователь разблокирован');
        } catch (error) { alert("Ошибка при блокировке"); }
    };

    const handleToggleGlobal = async () => {
        if (!window.confirm("Изменить статус Глобального чата?")) return;
        try {
            await API.post('/api/chats/global/toggle/');
            fetchChats();
            if (activeChat?.type === 'global') fetchMessages();
        } catch (error) { alert("Ошибка изменения статуса"); }
    };

    const getChatIcon = (type) => {
        if (type === 'global') return '🌍';
        if (type === 'group') return '🎓';
        return '👤';
    };

    if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

    return (
        <div className="min-h-screen bg-gray-100 p-4 font-sans">
            <div className="max-w-[1600px] mx-auto bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden flex h-[calc(100vh-100px)]">
                
                {/* ЛЕВАЯ КОЛОНКА */}
                <div className="w-full md:w-80 border-r border-gray-200 flex flex-col bg-gray-50/50">
                    <div className="p-4 border-b border-gray-200 bg-white">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-black text-gray-800">Сообщения</h2>
                            
                            {/* Настройки приватности */}
                            <div className="relative">
                                <button onClick={() => setIsPrivacyMenuOpen(!isPrivacyMenuOpen)} className="text-gray-400 hover:text-gray-800 transition-colors">⚙️</button>
                                {isPrivacyMenuOpen && (
                                    <div className="absolute right-0 top-6 w-48 bg-white rounded-xl shadow-xl py-2 border border-gray-100 z-50">
                                        <button onClick={handleToggleMyPrivacy} className="w-full text-left px-4 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center justify-between">
                                            <span>Принимать ЛС</span>
                                            <span className={`w-8 h-4 rounded-full relative ${myChatEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                                                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${myChatEnabled ? 'right-0.5' : 'left-0.5'}`}></span>
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Переключатель режимов: Чаты / Поиск */}
                        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-3">
                            <button onClick={() => setSearchMode(false)} className={`flex-1 text-[10px] font-bold py-1.5 rounded transition-all ${!searchMode ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-800'}`}>Мои чаты</button>
                            <button onClick={() => setSearchMode(true)} className={`flex-1 text-[10px] font-bold py-1.5 rounded transition-all ${searchMode ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-800'}`}>Найти людей</button>
                        </div>

                        {searchMode && (
                            <>
                                <input type="text" placeholder="Поиск по имени..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-gray-100 border-none rounded-lg px-3 py-2 text-xs font-semibold outline-none focus:ring-2 focus:ring-blue-500/20 mb-2" />
                                <div className="flex gap-1">
                                    {['ALL', 'TEACHER', 'STUDENT'].map(role => (
                                        <button key={role} onClick={() => setRoleFilter(role)} className={`flex-1 text-[9px] font-bold py-1 rounded transition-colors ${roleFilter === role ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {role === 'ALL' ? 'Все' : role === 'TEACHER' ? 'Учителя' : 'Ученики'}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* СПИСКИ (Чаты или Поиск) */}
                    <div className="flex-1 overflow-y-auto">
                        {!searchMode ? (
                            chats.map(chat => (
                                <div key={chat.id} onClick={() => setActiveChat(chat)} className={`p-4 border-b border-gray-100 cursor-pointer transition-all flex items-center gap-3 ${activeChat?.id === chat.id ? 'bg-blue-50' : 'hover:bg-white'}`}>
                                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-xl shadow-inner shrink-0 relative">
                                        {getChatIcon(chat.type)}
                                        {chat.isOnline && chat.type === 'private' && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-bold text-gray-900 truncate">{chat.name}</h3>
                                        <p className="text-xs text-gray-500 truncate">{chat.lastMessage}</p>
                                    </div>
                                    {chat.unread > 0 && <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-[10px] font-black text-white shrink-0 animate-pulse">{chat.unread > 99 ? '99+' : chat.unread}</div>}
                                </div>
                            ))
                        ) : (
                            searchResults.length === 0 ? <p className="text-center text-xs text-gray-400 mt-6 font-bold">Никто не найден</p> :
                            searchResults.map(user => (
                                <div key={user.id} onClick={() => handleStartPrivateChat(user.id)} className="p-3 border-b border-gray-100 cursor-pointer transition-all hover:bg-white flex items-center gap-3 group">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-lg relative">
                                        👤{user.isOnline && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-bold text-gray-800 truncate">{user.name}</h3>
                                        <span className={`text-[9px] font-black uppercase tracking-widest ${user.role === 'TEACHER' ? 'text-blue-500' : 'text-purple-500'}`}>{user.role === 'TEACHER' ? 'Учитель' : 'Ученик'}</span>
                                    </div>
                                    <span className="text-[10px] font-bold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">Написать</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* ПРАВАЯ КОЛОНКА: ОКНО ЧАТА */}
                <div className={`flex-1 flex flex-col bg-[#F8F9FA] ${!activeChat ? 'hidden md:flex' : 'flex'}`}>
                    {!activeChat ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                            <span className="text-6xl mb-4">💬</span>
                            <h2 className="text-xl font-bold text-gray-600">Выберите чат</h2>
                            <p className="text-sm font-medium mt-2">или найдите собеседника в поиске</p>
                        </div>
                    ) : (
                        <>
                            {/* ШАПКА ЧАТА */}
                            <div className="h-16 border-b border-gray-200 bg-white px-6 flex items-center justify-between shadow-sm z-10">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-lg">{getChatIcon(activeChat.type)}</div>
                                    <div>
                                        <h2 className="text-sm font-black text-gray-800">{activeChat.name}</h2>
                                        {activeChat.type === 'global' && !activeChat.is_active && <span className="text-[10px] font-bold text-red-500 uppercase">Чат отключен</span>}
                                        {activeChat.type === 'global' && activeChat.is_active && <span className="text-[10px] font-bold text-green-500 uppercase">Онлайн</span>}
                                        {activeChat.type === 'private' && isCurrentChatBlocked && <span className="text-[10px] font-bold text-red-500 uppercase">Пользователь заблокирован</span>}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {activeChat.type === 'private' && chatTargetUserId && (
                                        <button onClick={handleBlockUser} className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${isCurrentChatBlocked ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-red-50 text-red-600 border-red-100'}`}>
                                            {isCurrentChatBlocked ? 'Разблокировать' : '🚫 Заблокировать'}
                                        </button>
                                    )}
                                    {isAdmin && activeChat.type === 'global' && (
                                        <button onClick={handleToggleGlobal} className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${activeChat.is_active ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-600 border-green-100'}`}>
                                            {activeChat.is_active ? 'Отключить чат' : 'Включить чат'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* ИСТОРИЯ СООБЩЕНИЙ (ДОБАВЛЕН chatContainerRef) */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4" ref={chatContainerRef}>
                                {messages.map((msg, idx) => {
                                    const isMine = msg.sender_id === currentUserId;
                                    const showName = !isMine && (idx === 0 || messages[idx-1].sender_id !== msg.sender_id);
                                    
                                    return (
                                        <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                                                {showName && activeChat.type !== 'private' && <span className="text-[10px] font-bold text-gray-500 ml-1 mb-1">{msg.sender_name}</span>}
                                                <div className={`px-4 py-2 rounded-2xl shadow-sm text-sm font-medium ${isMine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'}`}>
                                                    {msg.text}
                                                </div>
                                                <span className="text-[9px] font-semibold text-gray-400 mt-1 mx-1">
                                                    {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* ПОЛЕ ВВОДА */}
                            <div className="p-4 bg-white border-t border-gray-200">
                                {isCurrentChatBlocked ? (
                                    <div className="text-center text-xs font-bold text-red-400 py-2">Вы заблокировали этого пользователя.</div>
                                ) : activeChat.type === 'global' && !activeChat.is_active && !isAdmin ? (
                                    <div className="text-center text-xs font-bold text-gray-400 py-2">Администратор временно отключил глобальный чат.</div>
                                ) : (
                                    <form onSubmit={handleSendMessage} className="flex gap-2 relative">
                                        <input 
                                            type="text" 
                                            placeholder="Написать сообщение..." 
                                            className="flex-1 bg-gray-100 border-none rounded-xl pl-4 pr-12 py-3 text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                        />
                                        <button type="submit" disabled={!newMessage.trim()} className="absolute right-2 top-1.5 bottom-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg px-4 font-bold text-sm transition-all shadow-sm">
                                            ➤
                                        </button>
                                    </form>
                                )}
                            </div>
                        </>
                    )}
                </div>

            </div>
        </div>
    );
}